import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Truck, CheckCircle2, Package, Boxes, Store, XCircle } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { StatusPill } from "@/components/Pills";
import { useLang } from "@/context/LanguageContext";

// Phase C (Feb 2026): Salesman dashboard drilldowns.
// Variants:
//   delivered → only delivered today
//   pending   → pending today
//   boxes     → product-wise boxes sold today
//   shops     → all shops the salesman has visited (today's orders' shops)
//   nosale    → no-sale records list with reasons
const REASON_LABEL = {
  shop_closed: "reason_shop_closed",
  already_has_stock: "reason_already_has_stock",
  other_company: "reason_other_company",
  owner_absent: "reason_owner_absent",
  asking_credit: "reason_asking_credit",
  price_issue: "reason_price_issue",
  other: "reason_other",
};

const VARIANTS = {
  delivered: { titleKey: "delivered_today", color: "bg-[#34C759]", icon: CheckCircle2, type: "orders", filter: o => o.status === "delivered" },
  pending:   { titleKey: "pending",         color: "bg-[#FF9500]", icon: Package,      type: "orders", filter: o => ["pending","assigned","out_for_delivery"].includes(o.status) },
  boxes:     { titleKey: "boxes_sold",      color: "bg-[#5856D6]", icon: Boxes,        type: "boxes" },
  shops:     { titleKey: "total_shops",     color: "bg-[#0055FF]", icon: Store,        type: "shops" },
  nosale:    { titleKey: "not_bought",      color: "bg-[#FF3B30]", icon: XCircle,      type: "nosale" },
};

export default function SalesmanDrilldown() {
  const nav = useNavigate();
  const { variant = "delivered" } = useParams();
  const cfg = VARIANTS[variant] || VARIANTS.delivered;
  const { t, lang, displayName } = useLang();
  const [orders, setOrders] = useState([]);
  const [shops, setShops] = useState([]);
  const [noSales, setNoSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (cfg.type === "orders" || cfg.type === "boxes") {
      api.get("/orders").then(r => setOrders(r.data || [])).finally(() => setLoading(false));
    } else if (cfg.type === "shops") {
      api.get("/shops").then(r => setShops(r.data || [])).finally(() => setLoading(false));
    } else if (cfg.type === "nosale") {
      api.get("/no-sales").then(r => setNoSales(r.data || [])).finally(() => setLoading(false));
    }
  // eslint-disable-next-line
  }, [variant]);

  const filteredOrders = useMemo(() => {
    if (cfg.type !== "orders") return [];
    return [...orders].filter(cfg.filter).sort((a, b) => (b.total_units || 0) - (a.total_units || 0));
  }, [orders, cfg]);

  // Product-wise boxes-sold breakdown — aggregate from delivered orders only
  const boxesByProduct = useMemo(() => {
    if (cfg.type !== "boxes") return [];
    const map = {};
    for (const o of orders) {
      if (o.status !== "delivered") continue;
      for (const it of (o.items || [])) {
        const k = it.product_id;
        if (!map[k]) map[k] = { id: k, name: it.name, name_en: it.name_en, name_ta: it.name_ta, qty: 0 };
        map[k].qty += it.quantity || 0;
      }
    }
    return Object.values(map).sort((a, b) => b.qty - a.qty);
  }, [orders, cfg.type]);

  const totalBoxes = useMemo(() => {
    if (cfg.type === "orders") return filteredOrders.reduce((s, o) => s + (o.total_units || 0), 0);
    if (cfg.type === "boxes")  return boxesByProduct.reduce((s, r) => s + r.qty, 0);
    return 0;
  }, [cfg.type, filteredOrders, boxesByProduct]);

  const itemCount = cfg.type === "orders" ? filteredOrders.length
    : cfg.type === "boxes" ? boxesByProduct.length
    : cfg.type === "shops" ? shops.length
    : noSales.length;

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar title={t(cfg.titleKey)} onBack={() => nav("/salesman")} />
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Hero */}
        <div className={`${cfg.color} text-white rounded-3xl p-5 shadow-xl`} data-testid="sd-summary">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 grid place-items-center">
              <cfg.icon className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-80 font-bold">{t(cfg.titleKey)}</div>
              <div className="heading-font text-3xl font-bold">{itemCount}</div>
              {(cfg.type === "orders" || cfg.type === "boxes") && (
                <div className="text-xs opacity-80 mt-0.5 flex items-center gap-1">
                  <Boxes className="w-3.5 h-3.5" /> {totalBoxes} {t("boxes")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#86868B]">{t("loading")}</div>
          ) : itemCount === 0 ? (
            <div className="p-12 text-center text-[#86868B]" data-testid="sd-empty">
              {lang === "ta" ? "எதுவும் இல்ல" : "Nothing to show"}
            </div>
          ) : cfg.type === "orders" ? (
            <div className="divide-y divide-gray-100">
              {filteredOrders.map((o, idx) => (
                <button
                  key={o.id}
                  onClick={() => nav(`/salesman/order/${o.id}`)}
                  data-testid={`sd-order-${o.id}`}
                  className="w-full p-4 sm:p-5 text-left hover:bg-gray-50/50 transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-[#86868B] tabular-nums">#{idx + 1}</span>
                        <div className="heading-font font-bold text-[#1D1D1F] truncate">{o.shop_name}</div>
                        <StatusPill status={o.status} t={t} />
                      </div>
                      <div className="text-xs text-[#86868B] mt-1">
                        {new Date(o.created_at).toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="heading-font text-2xl font-bold text-[#1D1D1F]">{o.total_units || 0}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{t("boxes")}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : cfg.type === "boxes" ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">{t("name")}</th>
                  <th className="px-4 py-3">{t("boxes_sold")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {boxesByProduct.map((r, i) => (
                  <tr key={r.id} className="hover:bg-gray-50/40" data-testid={`sd-boxes-${i}`}>
                    <td className="px-4 py-3 font-bold text-[#86868B]">{i + 1}</td>
                    <td className="px-4 py-3 font-semibold heading-font">
                      {lang === "ta" ? (r.name_ta || r.name_en || r.name) : (r.name_en || r.name)}
                    </td>
                    <td className="px-4 py-3 font-bold text-[#1D1D1F]">{r.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : cfg.type === "shops" ? (
            <div className="divide-y divide-gray-100">
              {shops.map((s) => (
                <div key={s.id} className="p-4 sm:p-5" data-testid={`sd-shop-${s.id}`}>
                  <div className="heading-font font-bold text-[#1D1D1F]">{lang === "ta" ? (s.name_ta || s.name_en || s.name) : (s.name_en || s.name)}</div>
                  <div className="text-xs text-[#86868B] mt-1">
                    {s.shop_type ? <span className="pill bg-blue-50 text-blue-700 mr-2">{t(s.shop_type)}</span> : null}
                    {s.phone ? `📞 ${s.phone}` : ""}
                  </div>
                  {s.address ? <div className="text-xs text-[#86868B] mt-1 truncate">{s.address}</div> : null}
                </div>
              ))}
            </div>
          ) : (
            // nosale
            <div className="divide-y divide-gray-100">
              {noSales.map((ns) => (
                <div key={ns.id} className="p-4 sm:p-5" data-testid={`sd-nosale-${ns.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="heading-font font-bold text-[#1D1D1F]">{ns.shop_name}</div>
                      <div className="text-xs text-[#86868B] mt-1 flex flex-wrap gap-2">
                        {ns.phone && <span>📞 {ns.phone}</span>}
                        <span>{new Date(ns.created_at).toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}</span>
                      </div>
                    </div>
                    <span className="pill bg-red-50 text-red-700 text-[11px] shrink-0">
                      {ns.reason_code === "other" ? (ns.custom_reason || t("reason_other")) : t(REASON_LABEL[ns.reason_code] || "reason")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
